'use client';

import { Activity, Suspense, useRef } from 'react';
import { usePathname } from 'next/navigation';
import {
  Anchor,
  AppShellHeader,
  Box,
  Group,
  Image,
  MantineSpacing,
  Skeleton,
  Stack,
  StyleProp,
  useMatches,
} from '@mantine/core';
import { useDisclosure, useMounted } from '@mantine/hooks';
import { useHasCompleteAuthToken } from '@/src/modules/auth/hooks/use-has-complete-auth-token';
import { useIsAuthenticated } from '@/src/modules/auth/hooks/use-is-authenticated';
import { useAuthStore } from '@/src/modules/auth/stores/use-auth-store';
import { useGetCartCount } from '@/src/modules/marketplace/cart/hooks/use-get-cart';
import { HeaderActionListAuthenticated } from './action-list/header-action-list-authenticated';
import { HeaderActionListUnauthenticated } from './action-list/header-action-list-unauthenticated';
import { AddressInput } from './actions/address-input';
import { BackButton } from './actions/back-button';
import { MobileSearchInput } from './actions/mobile-search-input';
import { SearchInput } from './actions/search-input';
import { MigrationBanner } from './migration-banner';

const headerHorizontalSpacing: StyleProp<MantineSpacing> = {
  base: 'md',
  md: '4rem',
  lg: '8rem',
};

interface IHeaderProps {
  migrationBannerShown?: boolean;
  onHideMigrationBanner?: () => void;
}

export function Header({
  migrationBannerShown = false,
  onHideMigrationBanner,
}: IHeaderProps) {
  const pathname = usePathname();
  const isMounted = useMounted();
  const isMobileView = useMatches({ base: true, md: false });
  const userId = useAuthStore((state) => state.user?.props.id);
  const authStatus = useAuthStore((state) => state.status);
  const isAuthenticated = useIsAuthenticated();
  const horizontalSpacing = useMatches({ base: 'xs', md: 'sm' });
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);
  const hasCompleteAuthToken = useHasCompleteAuthToken();
  const isAuthenticating =
    hasCompleteAuthToken && ['idle', 'loading'].includes(authStatus);

  const { data: cartItemCount = 0 } = useGetCartCount(
    isAuthenticated && userId ? { user_id: userId } : null
  );
  const [
    mobileSearchInputVisibility,
    {
      open: showMobileSearchInputVisibility,
      close: hideMobileSearchInputVisibility,
    },
  ] = useDisclosure();

  const showBackButton = pathname !== '/' && isMobileView;

  const handleSearchInputOnClickOnMobile = (
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    event.preventDefault();
    showMobileSearchInputVisibility();
    const focusOnMobileSearchInput = setTimeout(
      () => mobileSearchInputRef.current?.focus(),
      110
    );
    return () => clearTimeout(focusOnMobileSearchInput);
  };

  if (!isMounted) {
    return null;
  }

  return (
    <AppShellHeader>
      <Stack gap={0} h="100%">
        {migrationBannerShown && (
          <MigrationBanner onClose={onHideMigrationBanner} />
        )}

        <Box
          px={headerHorizontalSpacing}
          pl={showBackButton ? '0.3rem' : headerHorizontalSpacing}
          flex={1}
        >
          {isMobileView && (
            <Activity mode={mobileSearchInputVisibility ? 'visible' : 'hidden'}>
              <Suspense fallback={<Skeleton w="100%" h={42} />}>
                <MobileSearchInput
                  ref={mobileSearchInputRef}
                  onClose={hideMobileSearchInputVisibility}
                />
              </Suspense>
            </Activity>
          )}

          <Activity mode={mobileSearchInputVisibility ? 'hidden' : 'visible'}>
            <Group
              h={isAuthenticated ? '60%' : '100%'}
              gap={showBackButton ? '0.5rem' : 0}
            >
              {showBackButton && <BackButton />}

              <Group gap={horizontalSpacing} flex={1}>
                <Anchor href="/" visibleFrom="md">
                  <Image src="/images/logo.png" alt="Healthyone Logo" w="7em" />
                </Anchor>

                <Box
                  flex={1}
                  onClick={
                    isMobileView ? handleSearchInputOnClickOnMobile : undefined
                  }
                >
                  <Suspense fallback={<Skeleton w="100%" h={42} />}>
                    <SearchInput />
                  </Suspense>
                </Box>

                {!isAuthenticating && (
                  <Group gap={horizontalSpacing}>
                    {isAuthenticated ? (
                      <HeaderActionListAuthenticated
                        cartItemCount={cartItemCount}
                      />
                    ) : (
                      <HeaderActionListUnauthenticated />
                    )}
                  </Group>
                )}
              </Group>
            </Group>

            {isAuthenticated && (
              <Group
                gap={showBackButton ? '0.5rem' : 0}
                justify={isMobileView ? 'center' : 'flex-end'}
              >
                <AddressInput />
              </Group>
            )}
          </Activity>
        </Box>
      </Stack>
    </AppShellHeader>
  );
}
